// src/components/staff/StaffList.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { AgGridReact } from 'ag-grid-react';
import {
    Typography, Button, Box, TextField, InputAdornment, Card,
    Stack, IconButton, Fade, LinearProgress, Avatar, Tabs, Tab, Chip
} from "@mui/material";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
import { useNavigate } from 'react-router-dom';
import {
    Search as SearchIcon,
    Add as AddIcon,
    FileDownload as FileDownloadIcon,
    Refresh as RefreshIcon,
    Group as GroupIcon,
    Visibility as VisibilityIcon,
    Edit as EditIcon,
    PersonAdd as RehireIcon,
    CloudUpload as UploadIcon
} from '@mui/icons-material';

import AppHeader from '../AppHeader';
import RehireSearchModal from './components/RehireSearchModal';

import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import {
    CheckCircle as CheckCircleIcon,
    Domain as DomainIcon
} from '@mui/icons-material';

ModuleRegistry.registerModules([AllCommunityModule]);

const StaffList = ({ setIsLoggedIn }) => {
    const [rowData, setRowData] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [listTab, setListTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const gridRef = useRef(null);
    const navigate = useNavigate();
    const tenant = useMemo(() => localStorage.getItem("tenant") || "", []);

    // Dynamic SOM Columns State
    const [somConfig, setSomConfig] = useState(null);
    const [orgNodes, setOrgNodes] = useState([]);
    const [orgTypes, setOrgTypes] = useState([]);
    const [hierarchyMap, setHierarchyMap] = useState({}); // Pre-calculated paths
    const [user, setUser] = useState({ role: '', permissions: [] });

    useEffect(() => {
        const stored = sessionStorage.getItem("user");
        if (stored) setUser(JSON.parse(stored));
    }, []);

    // --- Custom Cell Renderers (Premium Guidelines) ---
    const StatusBadge = useCallback((params) => {
        const displayStatus = params.data?.display_status || params.data?.employment_status;
        const isNoticePeriod = displayStatus === 'NOTICE_PERIOD';
        const isResignedOrTerminated = ['RESIGNED', 'TERMINATED'].includes(displayStatus);
        // For non-resigned staff, also check is_active
        const isActive = isNoticePeriod || params.value === 1 || params.value === true;

        let color = '#10b981';
        let label = 'Active';
        let textColor = '#059669';

        if (isNoticePeriod) {
            color = '#f97316';
            label = 'Notice Period';
            textColor = '#c2410c';
        } else if (isResignedOrTerminated) {
            color = '#ef4444';
            label = displayStatus === 'RESIGNED' ? 'Resigned' : 'Terminated';
            textColor = '#dc2626';
        } else if (!isActive) {
            color = '#94a3b8';
            label = 'Inactive';
            textColor = '#64748b';
        }

        return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                <Box sx={{
                    width: 8, height: 8, borderRadius: '50%',
                    bgcolor: color, mr: 1,
                    boxShadow: (!isResignedOrTerminated && !isNoticePeriod) && isActive ? `0 0 0 2px ${color}20` : 'none'
                }} />
                <Typography variant="body2" sx={{ color: textColor, fontWeight: 600, fontSize: '0.85rem' }}>
                    {label}
                </Typography>
            </Box>
        );
    }, []);

    const WorkTypeBadge = useCallback((params) => {
        const isShift = params.value === 'shift_based';
        return (
            <Box sx={{
                display: 'inline-flex', px: 1.5, py: 0.5, borderRadius: 2,
                bgcolor: isShift ? '#f5f3ff' : '#eff6ff',
                border: '1px solid',
                borderColor: isShift ? '#ddd6fe' : '#dbeafe',
                alignItems: 'center'
            }}>
                <Typography variant="caption" sx={{ color: isShift ? '#7c3aed' : '#2563eb', fontWeight: 800, fontSize: 10 }}>
                    {isShift ? 'SHIFT-BASED' : 'OFFICE-FIXED'}
                </Typography>
            </Box>
        );
    }, []);

    const FullNameRenderer = useCallback((params) => (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ height: '100%' }}>
            <Avatar
                src="/default-avatar.png"
                sx={{ width: 28, height: 28, fontSize: 11, fontWeight: 800, bgcolor: '#f1f5f9', color: 'primary.main', border: '1px solid #e2e8f0' }}
            >
                {params.value?.charAt(0).toUpperCase()}
            </Avatar>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.85rem' }}>
                {params.value}
            </Typography>
        </Stack>
    ), []);

    const ActionRenderer = useCallback((params) => {
        const r = user.role?.toLowerCase();
        const canEdit = user.permissions?.includes('MANAGE_STAFF') || ['super_admin', 'support_admin', 'hr_manager', 'hr', 'hr_branch'].includes(r);
        return (
            <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center" sx={{ height: '100%' }}>
                <Tooltip title="View Profile">
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate('/staff/' + params.data.staff_id); }} sx={{ color: 'primary.main', '&:hover': { bgcolor: '#f5f3ff' } }}>
                        <VisibilityIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                {canEdit && (
                    <Tooltip title="Edit Profile">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/staff/edit/${params.data.staff_id}`); }} sx={{ color: '#10b981', '&:hover': { bgcolor: '#ecfdf5' } }}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Stack>
        );
    }, [navigate, user.role, user.permissions]);

    // --- Pre-calculate all hierarchy paths once ---
    useEffect(() => {
        if (orgNodes.length === 0 || orgTypes.length === 0) return;

        const nodesMap = {};
        orgNodes.forEach(n => { nodesMap[String(n.id)] = n; });

        const newMap = {};
        orgNodes.forEach(node => {
            const path = {};
            let current = node;
            let safety = 0;
            while (current && safety < 10) {
                path[String(current.type_id)] = current.node_name;
                current = nodesMap[current.parent_id];
                safety++;
            }
            newMap[String(node.id)] = path;
        });
        setHierarchyMap(newMap);
    }, [orgNodes, orgTypes]);

    const columnDefs = useMemo(() => {
        const cols = [
            {
                headerName: "Employee ID",
                field: "employee_number",
                pinned: "left",
                width: 140,
                cellStyle: { fontWeight: 800, color: 'primary.main' }
            },
            {
                headerName: "Full Name",
                field: "full_name",
                width: 220,
                pinned: 'left',
                cellRenderer: FullNameRenderer
            }
        ];

        if (somConfig && somConfig.active_levels_json && orgNodes.length > 0) {
            const nodesMap = {};
            orgNodes.forEach(n => { nodesMap[String(n.id)] = n; });

            const activeLevels = somConfig.active_levels_json || [];
            const levelsToShow = activeLevels.slice(1);

            levelsToShow.forEach(levelName => {
                const targetType = orgTypes.find(t => t.type_name.toLowerCase() === levelName.toLowerCase());
                const typeId = targetType ? String(targetType.id) : null;

                cols.push({
                    headerName: levelName,
                    field: `som_${levelName}`,
                    width: 130,
                    valueGetter: (params) => {
                        const nodeId = params.data.org_node_id;
                        if (!nodeId) return params.data.department && levelName.toLowerCase().includes("department") ? params.data.department : "-";

                        const path = hierarchyMap[String(nodeId)];
                        const resolvedVal = (path && typeId) ? path[typeId] : null;
                        if (!resolvedVal && levelName.toLowerCase().includes("department") && params.data.department) {
                            return params.data.department;
                        }
                        return resolvedVal || "-";
                    }
                });
            });

            if (somConfig.use_branches) {
                cols.push({ headerName: "Branch", field: "branch_name", width: 160 });
            }

            if (somConfig.use_job_catalogue) {
                cols.push(
                    { headerName: "Job Family", field: "job_name", width: 160 },
                    { headerName: "Job Title", field: "job_role_name", width: 160, cellStyle: { fontWeight: 500 } },
                    {
                        headerName: "Grade", field: "grade_name", width: 170,
                        valueFormatter: p => p.value ? `${p.value}${p.data.gradeset_name ? ` (${p.data.gradeset_name})` : ''}` : "-"
                    }
                );
            }
        }

        cols.push(
            {
                headerName: "Gender",
                field: "gender",
                width: 100,
                valueFormatter: p => p.value ? p.value.charAt(0).toUpperCase() + p.value.slice(1).toLowerCase() : "-"
            },
            {
                headerName: "Username",
                field: "username",
                width: 130,
            },
            { headerName: "Status", field: "is_active", width: 110, cellRenderer: StatusBadge },
            { headerName: "Type", field: "work_type", width: 110, cellRenderer: WorkTypeBadge },
            { headerName: "Reporting Head", field: "manager_name", width: 160 },
            {
                headerName: "Actions",
                field: "staff_id",
                width: 100,
                pinned: 'right',
                cellRenderer: ActionRenderer,
                sortable: false,
                filter: false
            }
        );

        return cols;
    }, [somConfig, orgNodes, orgTypes, hierarchyMap, StatusBadge, WorkTypeBadge, ActionRenderer, FullNameRenderer]);

    const defaultColDef = useMemo(() => ({
        sortable: true, filter: true, resizable: true, headerClass: 'premium-grid-header'
    }), []);

    const fetchStaffList = useCallback(async (isRefresh = false) => {
        // Caching: Try to load from sessionStorage if not a forced refresh
        if (!isRefresh) {
            const cachedStaff = sessionStorage.getItem(`staff_list_${tenant}`);
            const cachedSom = sessionStorage.getItem(`som_config_${tenant}`);
            if (cachedStaff && cachedSom) {
                setRowData(JSON.parse(cachedStaff));
                const som = JSON.parse(cachedSom);
                setSomConfig(som.config);
                setOrgNodes(som.nodes);
                setOrgTypes(som.types);
                // We still fetch in background to stay fresh, but don't show loader
            } else {
                setLoading(true);
            }
        } else {
            setLoading(true);
        }

        try {
            const h = { "X-Tenant": tenant };
            const [staffRes, configRes, nodesRes, typesRes] = await Promise.all([
                axios.get(process.env.REACT_APP_STAFF_API_URL + '/staff', { withCredentials: true, headers: h }),
                axios.get(`${process.env.REACT_APP_API_URL1}/som/config`, { withCredentials: true, headers: h }),
                axios.get(`${process.env.REACT_APP_API_URL1}/som/nodes`, { withCredentials: true, headers: h }),
                axios.get(`${process.env.REACT_APP_API_URL1}/som/types`, { withCredentials: true, headers: h })
            ]);

            const staffData = Array.isArray(staffRes.data) ? staffRes.data : (staffRes.data?.rows || []);
            setRowData(staffData);
            sessionStorage.setItem(`staff_list_${tenant}`, JSON.stringify(staffData));

            if (configRes.data.success) {
                let config = configRes.data.data;
                if (typeof config.active_levels_json === "string") config.active_levels_json = JSON.parse(config.active_levels_json);
                setSomConfig(config);

                const somData = {
                    config,
                    nodes: nodesRes.data.success 
                        ? [...nodesRes.data.data].sort((a, b) => {
                            const nameCompare = a.node_name.localeCompare(b.node_name);
                            if (nameCompare !== 0) return nameCompare;
                            return (a.level_order || 0) - (b.level_order || 0);
                        }) 
                        : [],
                    types: typesRes.data.success ? typesRes.data.data : []
                };
                setOrgNodes(somData.nodes);
                setOrgTypes(somData.types);
                sessionStorage.setItem(`som_config_${tenant}`, JSON.stringify(somData));
            }
        } catch (error) {
            console.error('Error fetching staff data:', error);
        } finally {
            setLoading(false);
        }
    }, [tenant]);

    useEffect(() => {
        // Clear cached staff list so display_status is always fresh
        sessionStorage.removeItem(`staff_list_${tenant}`);
        fetchStaffList();
    }, [fetchStaffList, tenant]);

    const onBtnExport = useCallback(() => gridRef.current.api.exportDataAsCsv(), []);
    const onRowClicked = useCallback(ev => navigate('/staff/' + ev.data.staff_id), [navigate]);
    const onBtnNewStaff = useCallback(() => navigate('/staff/new'), [navigate]);

    // Rehire modal state
    const [rehireSearchOpen, setRehireSearchOpen] = useState(false);

    const stats = useMemo(() => {
        const activeRows = rowData.filter(r => {
            const displayStatus = r.display_status || r.employment_status;
            // Notice Period = still employed (just pending last day)
            if (displayStatus === 'NOTICE_PERIOD') return true;
            // Fully resigned or terminated = not active
            if (['RESIGNED', 'TERMINATED'].includes(displayStatus)) return false;
            // Otherwise check is_active flag
            return r.is_active === 1 || r.is_active === true;
        });
        return {
            total: rowData.length,
            active: activeRows.length,
            shift: activeRows.filter(r => r.work_type === 'shift_based').length,
            office: activeRows.filter(r => r.work_type === 'office').length
        };
    }, [rowData]);

    // --- Tab-filtered data ---
    const filteredData = useMemo(() => {
        switch (listTab) {
            case 1: // Notice Period
                return rowData.filter(r => (r.display_status || r.employment_status) === 'NOTICE_PERIOD');
            case 2: // Resigned
                return rowData.filter(r => (r.display_status || r.employment_status) === 'RESIGNED');
            case 3: // Terminated
                return rowData.filter(r => (r.display_status || r.employment_status) === 'TERMINATED');
            default: // Active (0)
                return rowData.filter(r => {
                    const ds = r.display_status || r.employment_status;
                    if (ds === 'NOTICE_PERIOD') return true;
                    if (['RESIGNED', 'TERMINATED'].includes(ds)) return false;
                    return r.is_active === 1 || r.is_active === true;
                });
        }
    }, [rowData, listTab]);

    const tabCounts = useMemo(() => ({
        active: rowData.filter(r => {
            const ds = r.display_status || r.employment_status;
            if (ds === 'NOTICE_PERIOD') return true;
            if (['RESIGNED', 'TERMINATED'].includes(ds)) return false;
            return r.is_active === 1 || r.is_active === true;
        }).length,
        notice: rowData.filter(r => (r.display_status || r.employment_status) === 'NOTICE_PERIOD').length,
        resigned: rowData.filter(r => (r.display_status || r.employment_status) === 'RESIGNED').length,
        terminated: rowData.filter(r => (r.display_status || r.employment_status) === 'TERMINATED').length,
    }), [rowData]);

    const StatCard = ({ title, value, icon: Icon, color }) => (
        <Card sx={{ p: 1.5, flex: 1, borderRadius: 4, display: 'flex', alignItems: 'center', boxShadow: '0 4px 20px 0 rgba(0,0,0,0.02)', border: '1px solid #f1f5f9' }}>
            <Box sx={{ p: 1, borderRadius: 2.5, bgcolor: `${color}15`, mr: 1.5, display: 'flex' }}>
                <Icon sx={{ color: color, fontSize: 20 }} />
            </Box>
            <Box>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.65rem' }}>{title}</Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>{value}</Typography>
            </Box>
        </Card>
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f8fafc' }}>
            <AppHeader onLogout={() => { localStorage.clear(); setIsLoggedIn(false); navigate('/login'); }} />

            <Box sx={{ p: 2, flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 1.5, maxWidth: 1400, mx: 'auto', width: '100%' }}>
                <Stack direction="row" spacing={3}>
                    <StatCard title="Total Headcount" value={stats.total} icon={GroupIcon} color="primary.main" />
                    <StatCard title="Active Members" value={stats.active} icon={CheckCircleIcon} color="#10b981" />
                    <StatCard title="Office vs Shift" value={`${stats.office} / ${stats.shift}`} icon={DomainIcon} color="#f59e0b" />
                </Stack>

                <Fade in timeout={500}>
                    <Card sx={{ p: 1.5, px: 2, borderRadius: 4, boxShadow: '0 4px 20px 0 rgba(0,0,0,0.04)', bgcolor: '#fff' }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
                            <Box>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Box sx={{ p: 0.8, bgcolor: '#f1f5f9', borderRadius: 2, display: 'flex' }}>
                                        <GroupIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>
                                        Personnel Records
                                    </Typography>
                                </Stack>
                                <Typography variant="caption" sx={{ color: '#64748b', mt: 0.5, ml: 4.8, display: 'block' }}>
                                    Manage detailed employee profiles and operational status.
                                </Typography>
                            </Box>

                            <Stack direction="row" spacing={1.5}>
                                <TextField
                                    size="small"
                                    placeholder="Quick search..."
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                                            </InputAdornment>
                                        ),
                                        sx: { borderRadius: 2, bgcolor: '#f1f5f9', border: 'none', '& fieldset': { border: 'none' } }
                                    }}
                                    sx={{ width: 300 }}
                                />
                                <Tooltip title="Export to CSV">
                                    <IconButton onClick={onBtnExport} sx={{ bgcolor: '#f1f5f9' }}><FileDownloadIcon /></IconButton>
                                </Tooltip>
                                <Tooltip title="Refresh Directory">
                                    <IconButton onClick={() => fetchStaffList(true)} sx={{ bgcolor: '#f1f5f9' }}><RefreshIcon /></IconButton>
                                </Tooltip>
                                {user.permissions?.includes('EDIT_STAFF') && (
                                    <Tooltip title="Staff Import Centre">
                                        <IconButton onClick={() => navigate('/staff/migrate')} sx={{ bgcolor: '#f1f5f9', color: '#475569', '&:hover': { color: 'primary.main' } }}>
                                            <UploadIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {(user.permissions?.includes('MANAGE_STAFF') || ['super_admin', 'support_admin', 'hr_manager', 'hr', 'hr_branch'].includes(user.role?.toLowerCase())) && (
                                    <>
                                        <Button
                                            variant="outlined"
                                            startIcon={<RehireIcon />}
                                            sx={{
                                                borderColor: 'primary.main', color: 'primary.main', borderRadius: 2, px: 2.5, fontWeight: 700,
                                                textTransform: 'none', '&:hover': { bgcolor: '#ede9fe', borderColor: 'primary.main' }
                                            }}
                                            onClick={() => setRehireSearchOpen(true)}
                                        >
                                            Rehire
                                        </Button>
                                        <Button
                                            variant="contained"
                                            startIcon={<AddIcon />}
                                            sx={{
                                                bgcolor: 'primary.main', borderRadius: 2, px: 3, fontWeight: 700, textTransform: 'none',
                                                boxShadow: (theme) => `0 4px 12px ${theme.palette.primary.main}33`,
                                                '&:hover': { bgcolor: 'primary.dark' }
                                            }}
                                            onClick={onBtnNewStaff}
                                        >
                                            New Employee
                                        </Button>
                                    </>
                                )}
                            </Stack>
                        </Stack>
                    </Card>
                </Fade>

                <Paper sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 4px 20px 0 rgba(0,0,0,0.03)', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    {loading && <LinearProgress sx={{ '& .MuiLinearProgress-bar': { bgcolor: 'primary.main' } }} />}

                    {/* Status Tabs */}
                    <Tabs
                        value={listTab}
                        onChange={(e, v) => { setListTab(v); setSearchText(''); }}
                        sx={{
                            px: 2, borderBottom: '1px solid #f1f5f9', bgcolor: '#fcfcfc', minHeight: 44,
                            '& .MuiTab-root': { minHeight: 44, textTransform: 'none', fontWeight: 700, fontSize: '0.85rem', color: '#64748b', py: 0 },
                            '& .Mui-selected': { color: 'primary.main' },
                            '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0', bgcolor: 'primary.main' }
                        }}
                    >
                        <Tab label={
                            <Stack direction="row" spacing={1} alignItems="center">
                                <span>Active</span>
                                <Chip label={tabCounts.active} size="small" sx={{ height: 18, fontSize: '0.7rem', fontWeight: 800, bgcolor: '#dcfce7', color: '#15803d', '& .MuiChip-label': { px: 1 } }} />
                            </Stack>
                        } />
                        <Tab label={
                            <Stack direction="row" spacing={1} alignItems="center">
                                <span>Notice Period</span>
                                {tabCounts.notice > 0 && <Chip label={tabCounts.notice} size="small" sx={{ height: 18, fontSize: '0.7rem', fontWeight: 800, bgcolor: '#ffedd5', color: '#c2410c', '& .MuiChip-label': { px: 1 } }} />}
                            </Stack>
                        } />
                        <Tab label={
                            <Stack direction="row" spacing={1} alignItems="center">
                                <span>Resigned</span>
                                {tabCounts.resigned > 0 && <Chip label={tabCounts.resigned} size="small" sx={{ height: 18, fontSize: '0.7rem', fontWeight: 800, bgcolor: '#fee2e2', color: '#dc2626', '& .MuiChip-label': { px: 1 } }} />}
                            </Stack>
                        } />
                        <Tab label={
                            <Stack direction="row" spacing={1} alignItems="center">
                                <span>Terminated</span>
                                {tabCounts.terminated > 0 && <Chip label={tabCounts.terminated} size="small" sx={{ height: 18, fontSize: '0.7rem', fontWeight: 800, bgcolor: '#f1f5f9', color: '#475569', '& .MuiChip-label': { px: 1 } }} />}
                            </Stack>
                        } />
                    </Tabs>
                    <Box className="ag-theme-quartz" sx={{ flexGrow: 1, width: "100%", '& .premium-grid-header': { fontWeight: 800, color: '#475569' } }}>
                        <AgGridReact
                            ref={gridRef}
                            rowData={filteredData}
                            columnDefs={columnDefs}
                            defaultColDef={defaultColDef}
                            rowHeight={42}
                            headerHeight={42}
                            animateRows={true}
                            pagination={true}
                            paginationPageSize={10}
                            quickFilterText={searchText}
                            rowSelection={{ mode: "singleRow" }}
                            onRowClicked={onRowClicked}
                            suppressCellFocus={true}
                            overlayNoRowsTemplate="No records found in staff directory"
                            getRowStyle={params => {
                                const ds = params.data?.display_status || params.data?.employment_status;
                                if (params.data && ['RESIGNED', 'TERMINATED'].includes(ds)) {
                                    return { background: '#fef2f2' };
                                }
                                if (params.data && ds === 'NOTICE_PERIOD') {
                                    return { background: '#fff7ed' };
                                }
                                return null;
                            }}
                        />
                    </Box>
                </Paper>
            </Box>

            {/* Rehire Search Modal */}
            <RehireSearchModal
                open={rehireSearchOpen}
                onClose={() => setRehireSearchOpen(false)}
                tenant={tenant}
                onSelect={(s) => {
                    setRehireSearchOpen(false);
                    navigate('/staff/rehire', { state: { prefillStaffId: String(s.staff_id) } });
                }}
            />
        </Box>
    );
};

export default StaffList;
